import React, { useState, useEffect } from 'react';
import {
  Typography,
  CircularProgress,
  Button,
  Card,
  CardContent,
  List,
  // ListItem,
  // ListItemText,
  IconButton,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import { formatExpiryDate, getCardBrandIcon } from '../../utils/cardUtils';
import { safeFetchJson } from '../../utils/prerender';

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(3)
  },
  title: {
    marginBottom: theme.spacing(2),
    color: '#FFFFFF'
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2)
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  actionButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginLeft: theme.spacing(1)
  },
  actionButtonLabel: {
    fontSize: '0.7rem',
    marginTop: theme.spacing(0.5),
    color: '#8a8794',
    textAlign: 'center'
  },
  actionButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none'
  },
  card: {
    backgroundColor: '#211c27',
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    border: '1px solid #3a3643',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
      borderColor: '#6c63ff',
      transform: 'translateY(-2px)',
      boxShadow: '0 5px 15px rgba(0,0,0,0.2)'
    }
  },
  cardSelected: {
    borderColor: '#6c63ff',
    backgroundColor: 'rgba(108, 99, 255, 0.1)',
    boxShadow: '0 5px 15px rgba(108, 99, 255, 0.1)'
  },
  cardContent: {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2)
    },
    position: 'relative'
  },
  cardItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 0),
    cursor: 'pointer',
    flexWrap: 'wrap',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      paddingTop: theme.spacing(2)
    }
  },
  cardInfo: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(2),
    flex: 1,
    minWidth: 0
  },
  cardNumber: {
    color: '#FFFFFF',
    fontWeight: 500
  },
  cardExpiry: {
    color: '#8a8794',
    fontSize: '0.875rem'
  },
  cardBrand: {
    width: 40,
    height: 30,
    objectFit: 'contain'
  },
  defaultBadge: {
    color: '#6c63ff',
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.75rem',
    marginLeft: theme.spacing(1)
  },
  noCards: {
    color: '#8a8794',
    textAlign: 'center',
    padding: theme.spacing(3)
  },
  errorMessage: {
    color: '#ff6b6b',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2)
  },
  actionIcons: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1)
    }
  },
  iconButton: {
    color: '#8a8794',
    backgroundColor: 'rgba(58, 54, 67, 0.5)',
    padding: theme.spacing(1),
    margin: '0 4px',
    transition: 'all 0.2s ease',
    '&:hover': {
      color: '#FFFFFF',
      backgroundColor: 'rgba(108, 99, 255, 0.2)',
      transform: 'scale(1.1)'
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.75),
    }
  },
  defaultIconButton: {
    color: '#8a8794',
    backgroundColor: 'rgba(58, 54, 67, 0.5)',
    '&:hover': {
      color: '#ffd700',
      backgroundColor: 'rgba(255, 215, 0, 0.1)'
    }
  },
  deleteIconButton: {
    '&:hover': {
      color: '#ff6b6b',
      backgroundColor: 'rgba(255, 107, 107, 0.1)'
    }
  },
  starIcon: {
    color: '#ffd700'
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    [theme.breakpoints.down('xs')]: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1)
    }
  }
}));

const StripeCDKSavedCards = ({
  onSelectPaymentMethod,
  selectedPaymentMethodId,
  onSetDefault,
  onDelete,
  authToken
}) => {
  const classes = useStyles();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState(null);

  useEffect(() => {
    if (authToken) {
      fetchSavedCards();
    } else {
      setLoading(false);
      // setError('Authentication required to load saved cards.'); // Optional: English error for missing auth
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const fetchSavedCards = async () => {
    setLoading(true);
    setError(null);

    try {
      // Use safeFetchJson which returns null during prerender or when non-JSON is received
  const responseData = await safeFetchJson('/api/v1/user/payment-methods', { method: 'GET', headers: { 'Authorization': `JWT ${authToken}`, 'Content-Type': 'application/json' } });
      if (!responseData) {
        // No data (prerender or network blocked) — show empty list silently
        setPaymentMethods([]);
        setLoading(false);
        return;
      }

      setPaymentMethods(responseData.paymentMethods || []);
    } catch (error) {
      console.error('Error fetching saved cards:', error);
      setError(error.message || 'Could not load saved payment methods.');
    } finally {
      setLoading(false);
    }
  };

  const handleCardSelect = (paymentMethodId) => {
    if (onSelectPaymentMethod) {
      onSelectPaymentMethod(paymentMethodId);
    }
  };

  const handleSetDefault = async (paymentMethodId, event) => {
    event.stopPropagation(); // 防止触发卡片选择

    setError(null);
    try {
  const responseData = await safeFetchJson('/api/v1/user/payment-methods/default', { method: 'POST', headers: { 'Authorization': `JWT ${authToken}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ paymentMethodId }) });
      if (!responseData) {
        // prerender or blocked - swallow silently
        return;
      }

      // 更新本地状态
      setPaymentMethods(prevMethods =>
        prevMethods.map(method => ({
          ...method,
          isDefault: method.id === paymentMethodId
        }))
      );

      if (onSetDefault) {
        onSetDefault(paymentMethodId);
      }
    } catch (error) {
      console.error('Error setting default payment method:', error);
      setError(error.message || 'Could not set default payment method.');
    }
  };

  const openDeleteDialog = (paymentMethodId, event) => {
    event.stopPropagation(); // 防止触发卡片选择
    setPaymentMethodToDelete(paymentMethodId);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPaymentMethodToDelete(null);
  };

  const confirmDelete = async () => {
    if (!paymentMethodToDelete) return;

    setError(null);
    try {
        const responseData = await safeFetchJson(`/api/v1/user/payment-methods/${paymentMethodToDelete}`, { method: 'DELETE', headers: { 'Authorization': `JWT ${authToken}`, 'Content-Type': 'application/json' } });
        if (!responseData) {
          // prerender or blocked - assume success for build-time snapshot
        }

      // 从列表中移除
      setPaymentMethods(prevMethods =>
        prevMethods.filter(method => method.id !== paymentMethodToDelete)
      );

      // 如果删除的是当前选中的，取消选择
      if (selectedPaymentMethodId === paymentMethodToDelete && onSelectPaymentMethod) {
        onSelectPaymentMethod(null);
      }

      if (onDelete) {
        onDelete(paymentMethodToDelete);
      }
    } catch (error) {
      console.error('Error deleting payment method:', error);
      setError(error.message || 'Could not delete payment method.');
    } finally {
      closeDeleteDialog();
    }
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={24} />
        <Typography style={{ marginLeft: 8 }}>Loading payment methods...</Typography>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.root}>
        <Typography className={classes.errorMessage}>{error}</Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={fetchSavedCards}
          style={{ marginTop: 8 }}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (paymentMethods.length === 0 && !authToken) {
    return null;
  }

  if (paymentMethods.length === 0) {
    return (
      <div className={classes.root}>
        <Typography className={classes.noCards}>
          You have no saved payment methods
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.titleContainer}>
        <Typography variant="h6" className={classes.title}>
          Saved Payment Methods
        </Typography>

        <div className={classes.headerActions}>
          {/* 设为默认按钮 */}
          <Tooltip title={!selectedPaymentMethodId ? "Select a card first" : "Set as Default Card"} placement="top" arrow>
            <div className={classes.actionButton}>
              <IconButton
                size="small"
                className={`${classes.iconButton} ${classes.defaultIconButton} ${!selectedPaymentMethodId ? classes.actionButtonDisabled : ''}`}
                onClick={(e) => {
                  if (selectedPaymentMethodId) {
                    // 检查所选卡是否已经是默认卡
                    const selectedCard = paymentMethods.find(m => m.id === selectedPaymentMethodId);
                    if (selectedCard && !selectedCard.isDefault) {
                      handleSetDefault(selectedPaymentMethodId, e);
                    }
                  }
                }}
                disabled={!selectedPaymentMethodId}
              >
                {selectedPaymentMethodId && paymentMethods.find(m => m.id === selectedPaymentMethodId)?.isDefault
                  ? <StarIcon style={{ color: '#ffd700' }} />
                  : <StarBorderIcon />
                }
              </IconButton>
              <span className={classes.actionButtonLabel}>Default</span>
            </div>
          </Tooltip>

          {/* 删除按钮 */}
          <Tooltip title={!selectedPaymentMethodId ? "Select a card first" : "Delete Card"} placement="top" arrow>
            <div className={classes.actionButton}>
              <IconButton
                size="small"
                className={`${classes.iconButton} ${classes.deleteIconButton} ${!selectedPaymentMethodId ? classes.actionButtonDisabled : ''}`}
                onClick={(e) => {
                  if (selectedPaymentMethodId) {
                    openDeleteDialog(selectedPaymentMethodId, e);
                  }
                }}
                disabled={!selectedPaymentMethodId}
              >
                <DeleteIcon />
              </IconButton>
              <span className={classes.actionButtonLabel}>Delete</span>
            </div>
          </Tooltip>
        </div>
      </div>

      <List disablePadding>
        {paymentMethods.map((method) => (
          <Card
            key={method.id}
            className={`${classes.card} ${selectedPaymentMethodId === method.id ? classes.cardSelected : ''}`}
            onClick={() => handleCardSelect(method.id)}
          >
            <CardContent className={classes.cardContent}>
              <div className={classes.cardItem}>
                <Radio
                  checked={selectedPaymentMethodId === method.id}
                  onChange={() => handleCardSelect(method.id)}
                  color="primary"
                />

                <img
                  src={getCardBrandIcon(method.brand)}
                  alt={method.brand}
                  className={classes.cardBrand}
                />

                <div className={classes.cardInfo}>
                  <Typography className={classes.cardNumber}>
                    •••• •••• •••• {method.last4}
                    {method.isDefault && (
                      <span className={classes.defaultBadge}>
                        <StarIcon fontSize="small" style={{ marginRight: 4 }} /> Default
                      </span>
                    )}
                  </Typography>
                  <Typography className={classes.cardExpiry}>
                    {`Expires ${formatExpiryDate(method.expMonth, method.expYear)}`}
                  </Typography>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </List>

      {/* 删除确认对话框 */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this payment method? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default StripeCDKSavedCards;
